import {
    AccessibilityHelp, AutoLink, Autoformat, Autosave, BalloonToolbar, Bold, ClassicEditor, Code, CodeBlock, Essentials, EventInfo, FindAndReplace, GeneralHtmlSupport, Highlight, HtmlEmbed, Italic, Link, Paragraph, SelectAll, ShowBlocks, SourceEditing, Strikethrough, Table, TableCaption, TableCellProperties, TableColumnResize, TableProperties, TableToolbar, TextTransformation, Undo
} from 'ckeditor5';
import { CKEditor } from '@ckeditor/ckeditor5-react'

import './editor.css';

import { validateInput } from '../../utils/validators/Validators';
import { FormControl, FormHelperText, TextFieldProps, Typography } from '@mui/material';
import strings from 'common/Translation/Translate';
import { FC, useEffect, useRef, useState } from 'react';
import { FormValidators } from '../Form';

const READ_ONLY_LOCK = 'field-disabled';

interface OwnProps {
    leftIcon?: string;
    inputValue?: string;
    field?: string;
    typeValue?: string;
    textChange?: (value: string, field: string, error?: { error: boolean; message: string }) => void;
    validators?: FormValidators[];
    hasError?: boolean;
    fieldError?: boolean | string;
    multiline?: boolean;
    rows?: number | string;
    minRows?: number | string;
    maxRows?: number | string;
    infoText?: any;
    defaultSource?: boolean;
    placeholder?: string;
}

const CKEditor5: FC<OwnProps & TextFieldProps> = ({
    value,
    label,
    field = "editor",
    textChange,
    validators,
    hasError,
    fieldError,
    defaultSource = false,
    disabled,
    placeholder,
}) => {
    const [error, setError] = useState<{ error: boolean, message: string } | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const editorInstanceRef = useRef<ClassicEditor | null>(null);
    const disabledRef = useRef(Boolean(disabled));
    disabledRef.current = Boolean(disabled);

    useEffect(() => {
        const editor = editorInstanceRef.current;
        if (!editor) return;
        if (disabled) {
            editor.enableReadOnlyMode(READ_ONLY_LOCK);
            setIsEditing(false);
        } else {
            editor.disableReadOnlyMode(READ_ONLY_LOCK);
        }
    }, [disabled]);

    const handleChange = (_event: EventInfo<string, unknown>, editor: ClassicEditor) => {
        if (disabledRef.current) return;
        const newContent = editor.getData();
        console.log('ckeditor: ', newContent);
        const inputError = validateInput(validators, newContent);
        setError(inputError);
        textChange && textChange(newContent, field, inputError);
    };
    const editorContainerRef = useRef(null);
    const editorRef = useRef(null);
    const customMessage = () => (!error && hasError && fieldError ? strings.requiredText : '');

    const editorConfig = {
        licenseKey: 'GPL',
        highlight: {
            options: [
                {
                    model: 'yellowMarker',
                    class: 'marker-yellow',
                    title: strings.ckEditor_yellow_marker,
                    color: 'var(--ck-highlight-marker-yellow)',
                    type: 'marker'
                },
                {
                    model: 'greenMarker',
                    class: 'marker-green',
                    title: strings.ckEditor_green_marker,
                    color: 'var(--ck-highlight-marker-green)',
                    type: 'marker'
                },
                {
                    model: 'redPen',
                    class: 'pen-red',
                    title: strings.ckEditor_red_marker,
                    color: 'var(--ck-highlight-pen-red)',
                    type: 'pen'
                },
            ]
        },
        toolbar: {
            items: [
                'undo',
                'redo',
                '|',
                'SourceEditing',
                '|',
                'showBlocks',
                'findAndReplace',
                'selectAll',
                '|',
                'heading',
                '|',
                'bold',
                'italic',
                'strikethrough',
                'code',
                '|',
                'highlight',
                '|',
                'link',
                'insertTable',
                'codeBlock',
                //'htmlEmbed',
                '|',
                'accessibilityHelp'
            ],
            shouldNotGroupWhenFull: false
        },
        plugins: [
            AccessibilityHelp,
            Autoformat,
            AutoLink,
            Autosave,
            BalloonToolbar,
            Bold,
            Code,
            CodeBlock,
            Essentials,
            FindAndReplace,
            GeneralHtmlSupport,
            HtmlEmbed,
            Italic,
            Link,
            Paragraph,
            SelectAll,
            ShowBlocks,
            Table,
            TableCaption,
            TableCellProperties,
            TableColumnResize,
            TableProperties,
            TableToolbar,
            TextTransformation,
            // Title,
            Undo,
            SourceEditing,
            Strikethrough,
            Highlight
        ],
        balloonToolbar: ['bold', 'italic', '|', 'link'],
        // htmlSupport: {
        //     allow: [
        //         {
        //             name: /^.*$/,
        //             styles: true,
        //             attributes: true,
        //             classes: true
        //         }
        //     ]
        // },
        initialData: value,
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
            decorators: {
                toggleDownloadable: {
                    mode: 'manual',
                    label: 'Downloadable',
                    attributes: {
                        download: 'file'
                    }
                }
            }
        },
        menuBar: {
            isVisible: false
        },
        placeholder: placeholder || strings.ckEditorPlaceholder,
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
        },
    };
    return (
        <FormControl
            error={!!((error && error.error) || (!error && hasError && fieldError))}
            className={`dropdown`}
            fullWidth>
            {label && (
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 0.75,
                        fontSize: '1rem',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        color: disabled ? 'text.disabled' : 'text.primary',
                    }}>
                    {label}
                </Typography>
            )}
            <div
                ref={editorContainerRef}
                className={`ckeditor-field${isEditing && !disabled ? ' ckeditor-field--editing' : ''}${disabled ? ' ckeditor-field--disabled' : ''}`}>
                <div ref={editorRef}>
                    <CKEditor
                        disabled={disabled}
                        editor={ClassicEditor}
                        data={value as string}
                        onChange={handleChange}
                        onReady={(editor: ClassicEditor) => {
                            editorInstanceRef.current = editor;
                            if (disabled) {
                                editor.enableReadOnlyMode(READ_ONLY_LOCK);
                            }
                            setIsEditing(!disabledRef.current && editor.ui.focusTracker.isFocused);
                            editor.ui.focusTracker.on('change:isFocused', (_evt, _name, isFocused: boolean) => {
                                setIsEditing(!disabledRef.current && isFocused);
                            });
                        }}
                        config={editorConfig as any}

                    />
                </div>
            </div>
            <FormHelperText>{error && error.error ? error.message : customMessage()}</FormHelperText>
        </FormControl>
    )
}

export default CKEditor5;

